/**
 * Office homepage route nodes use a slug derived from `office.officeID`.
 * Decimal satellite IDs (e.g. 116.2) are stored on the route as hyphenated
 * segments (`116-2`) because URL/CSV slug validation rejects `.`.
 *
 * Use these helpers whenever comparing or building that homepage slug so
 * satellite offices match the same way as integer primary offices.
 *
 * GROQ fragments below are plain string constants so Sanity TypeGen can
 * inline them inside `groq` templates (it cannot evaluate function bodies).
 * Prefer `string::split` + `array::join` over `string::replace` — replace is
 * not available on the API version this project targets.
 */

/**
 * JS form of the office homepage route slug.
 * `116` → `"116"`, `116.2` → `"116-2"`.
 */
function toOfficeRouteSlug(officeID: number | string): string {
  return String(officeID).replace(/\./g, "-");
}

/**
 * GROQ expression that normalizes an officeID expression to the hyphenated
 * homepage route slug.
 *
 * Prefer the static fragments below inside `groq` templates that TypeGen
 * must parse; use this helper for scripts or dynamic query assembly.
 *
 * @example officeRouteSlugGroq("office.officeID")
 * // => `array::join(string::split(string(office.officeID), "."), "-")`
 */
function officeRouteSlugGroq(officeIDExpression: string): string {
  return `array::join(string::split(string(${officeIDExpression}), "."), "-")`;
}

/** GROQ: `office.officeID` as homepage route slug (`116.2` → `"116-2"`). */
const officeRouteSlugFromOffice =
  'array::join(string::split(string(office.officeID), "."), "-")';

/**
 * GROQ: parent `^.officeID` as homepage route slug (nested metrics queries).
 */
const officeRouteSlugFromParentOfficeID =
  'array::join(string::split(string(^.officeID), "."), "-")';

export {
  officeRouteSlugFromOffice,
  officeRouteSlugFromParentOfficeID,
  officeRouteSlugGroq,
  toOfficeRouteSlug,
};
